import { useParams } from 'react-router-dom';
import { useState } from 'react';
import ProductGallery from '@components/products/ProductGallery';
import RelatedProductsCarousel from '@components/products/RelatedProductsCarousel';
import Button from '@components/common/Button';
import { FiShoppingCart } from 'react-icons/fi';
import { useProduct } from '@hooks/useProduct';
import { useCart } from '@hooks/useCart';
import Loading from '@components/common/Loading';

/**
 * ProductDetailPage
 * Features:
 * - ProductGallery + ProductInfo
 * - Tabs: Details, Specs, Installation/Warranty
 * - RelatedProductsCarousel
 * - Add-to-Cart + installation toggle
 * - Animations: fade, slide
 */

const ProductDetailPage = () => {
  const { id } = useParams();
  const { product, relatedProducts, loading } = useProduct(id);
  const { addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('details');
  const [quantity, setQuantity] = useState(1);
  const [includeInstallation, setIncludeInstallation] = useState(false);

  const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'specs', label: 'Specifications' },
    { id: 'warranty', label: 'Warranty & Installation' },
  ];

  const handleAddToCart = () => {
    addToCart({
      ...product,
      quantity,
      includeInstallation,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading text="Loading product..." />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h1>
          <p className="text-gray-600">The product you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom">
        {/* Product Info Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Gallery */}
            <ProductGallery images={product.images || [product.image]} />

            {/* Product Details */}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{product.title}</h1>
              
              <div className="flex items-center space-x-4 mb-6">
                <span className="text-4xl font-bold text-secondary-600">
                  £{product.price?.toFixed(2)}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  product.stockStatus === 'in-stock' ? 'bg-green-100 text-green-600' :
                  product.stockStatus === 'low-stock' ? 'bg-orange-100 text-orange-600' :
                  'bg-red-100 text-red-600'
                }`}>
                  {product.stockStatus === 'in-stock' ? 'In Stock' :
                   product.stockStatus === 'low-stock' ? 'Low Stock' :
                   'Out of Stock'}
                </span>
              </div>

              <p className="text-gray-600 mb-8 leading-relaxed">
                {product.description}
              </p>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="label">Quantity</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    -
                  </button>
                  <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Installation Toggle */}
              <div className="mb-8">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeInstallation}
                    onChange={(e) => setIncludeInstallation(e.target.checked)}
                    className="h-5 w-5 text-secondary-500 focus:ring-secondary-400 border-gray-300 rounded"
                  />
                  <span className="text-gray-700">
                    Add Professional Installation <span className="font-semibold">(+£50)</span>
                  </span>
                </label>
              </div>

              {/* Add to Cart Button */}
              <Button
                text="Add to Cart"
                type="primary"
                onClick={handleAddToCart}
                disabled={product.stockStatus === 'out-of-stock'}
                className="w-full text-lg py-4"
                icon={<FiShoppingCart className="text-xl" />}
              />
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          {/* Tab Headers */}
          <div className="flex space-x-4 border-b border-gray-200 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-4 font-medium transition-colors duration-300 ${
                  activeTab === tab.id
                    ? 'text-secondary-600 border-b-2 border-secondary-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="prose max-w-none">
            {activeTab === 'details' && (
              <div>
                <h3>Product Details</h3>
                <p>{product.description}</p>
                {product.features && (
                  <ul>
                    {product.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {activeTab === 'specs' && (
              <div>
                <h3>Specifications</h3>
                <table className="w-full">
                  <tbody>
                    {product.specs ? Object.entries(product.specs).map(([key, value]) => (
                      <tr key={key} className="border-b">
                        <td className="py-2 font-semibold">{key}</td>
                        <td className="py-2">{value}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="2">No specifications available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'warranty' && (
              <div>
                <h3>Warranty & Installation</h3>
                <p>All products come with a comprehensive warranty and optional professional installation service.</p>
                <ul>
                  <li>2-year manufacturer warranty</li>
                  <li>Professional installation available</li>
                  <li>30-day return policy</li>
                  <li>24/7 customer support</li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts && relatedProducts.length > 0 && (
          <RelatedProductsCarousel
            products={relatedProducts}
            onAddToCart={addToCart}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;

